import {
  CARE_WATCH_VIEWS,
  DEFAULT_FILTERS,
  getDefaultFilters,
  isCareWatchLiveView,
} from "pages/CareWatch/utils/constants";
import {
  buildKpisFromOverview,
  filterSignals,
  paginateSignals,
  selectedCategories,
} from "pages/CareWatch/utils/queueUtils";

const toJS = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value.toJS === "function") return value.toJS();
  return value;
};

const getCareWatchState = (state) => state.careWatch;

export const getCareWatchItems = (state) =>
  toJS(getCareWatchState(state)?.get("items"), []);

export const getCareWatchListLoading = (state) =>
  Boolean(getCareWatchState(state)?.get("listLoading"));

export const getCareWatchListError = (state) =>
  getCareWatchState(state)?.get("listError") || "";

export const getCareWatchFilters = (state) =>
  toJS(getCareWatchState(state)?.get("filters"), DEFAULT_FILTERS);

/** Date range for Incident concerns and Missed documentation, even on Live. */
export const getCareWatchSignalDates = (state) => {
  const defaults = getDefaultFilters();
  const dates = toJS(getCareWatchState(state)?.get("signalDates"), null);
  return {
    fromDate: dates?.fromDate ?? defaults.fromDate,
    toDate: dates?.toDate ?? defaults.toDate,
  };
};

export const getCareWatchPage = (state) =>
  getCareWatchState(state)?.get("page") || 1;

export const getCareWatchPageSize = (state) =>
  getCareWatchState(state)?.get("pageSize") || 10;

export const getCareWatchSelectedId = (state) =>
  getCareWatchState(state)?.get("selectedId") || null;

export const getCareWatchCheckedIds = (state) =>
  toJS(getCareWatchState(state)?.get("checkedIds"), []);

export const getCareWatchActiveKpi = (state) =>
  getCareWatchState(state)?.get("activeKpi") || null;

export const getCareWatchPromoteHighlight = (state) =>
  Boolean(getCareWatchState(state)?.get("promoteHighlight"));

export const getCareWatchAcknowledgeLoading = (state) =>
  Boolean(getCareWatchState(state)?.get("acknowledgeLoading"));

export const getCareWatchPromoteLoading = (state) =>
  Boolean(getCareWatchState(state)?.get("promoteLoading"));

export const getCareWatchRiskSavingId = (state) =>
  getCareWatchState(state)?.get("riskSavingId") ?? null;

export const getCareWatchFiltered = (state) => {
  const items = getCareWatchItems(state);
  const filters = getCareWatchFilters(state);
  const activeKpi = getCareWatchActiveKpi(state);
  return filterSignals(items, filters, activeKpi);
};

export const getCareWatchTotal = (state) =>
  Number(getCareWatchState(state)?.get("total") || 0);

export const getCareWatchQueueView = (state) => {
  const page = getCareWatchPage(state);
  const pageSize = getCareWatchPageSize(state);
  const serverPaged = Boolean(getCareWatchState(state)?.get("serverPaged"));
  const filters = getCareWatchFilters(state);

  // Live is a single floor list — show every row, no pagination.
  // The server sends an unpaged array; cutting it to page size 10 here
  // made the tiles say "21 on the floor" while the table showed 10.
  if (isCareWatchLiveView(filters.view)) {
    const items = filterSignals(
      getCareWatchItems(state),
      filters,
      getCareWatchActiveKpi(state)
    );
    return {
      total: items.length,
      totalPages: 1,
      page: 1,
      pageSize: items.length || 1,
      pageItems: items,
    };
  }

  // The web table asks the server for one page. `total` is every matching
  // row, so the pager is not the length of the rows currently on screen.
  if (serverPaged) {
    const items = filterSignals(
      getCareWatchItems(state),
      filters,
      getCareWatchActiveKpi(state)
    );
    const total = getCareWatchTotal(state);
    return {
      total,
      totalPages: Math.max(1, Math.ceil(total / pageSize) || 1),
      page,
      pageSize,
      pageItems: items,
    };
  }

  return paginateSignals(getCareWatchFiltered(state), page, pageSize);
};

export const getCareWatchOverview = (state) =>
  toJS(getCareWatchState(state)?.get("overview"), {});

/**
 * KPI tiles, from the server's counts.
 *
 * The counts cover the current tab and status, matching the table query.
 * Counting the fetched page here would disagree as soon as there is more
 * than one page. The tile shape is unchanged.
 */
export const getCareWatchKpis = (state) => {
  const filters = getCareWatchFilters(state);
  return buildKpisFromOverview(getCareWatchOverview(state), filters.view);
};

export const getCareWatchDetailLoading = (state) =>
  Boolean(getCareWatchState(state)?.get("detailLoading"));

export const getCareWatchSelectedDetail = (state) =>
  toJS(getCareWatchState(state)?.get("selectedDetail"), null);

/**
 * The selected item, enriched with on-demand detail when available.
 *
 * The list returns lean rows (enough for the table). When a manager clicks
 * a row, the saga fetches the full signal into selectedDetail. This selector
 * merges the two so the detail dock gets all fields without re-fetching the
 * entire list.
 */
export const getCareWatchSelected = (state) => {
  const selectedId = getCareWatchSelectedId(state);
  if (!selectedId) return null;
  const { pageItems } = getCareWatchQueueView(state);
  const listItem = pageItems.find((item) => item.id === selectedId) || null;
  if (!listItem) return null;

  const detail = getCareWatchSelectedDetail(state);
  if (!detail) return listItem;

  // Merge: detail data overrides list data, list provides the baseline.
  // Keep list people when the detail payload has no structured people —
  // signal detail used to send display-name strings and wipe adapted
  // initials/names in the right panel.
  const detailPeople = detail.participants;
  const detailHasPeople =
    Array.isArray(detailPeople) &&
    detailPeople.length > 0 &&
    typeof detailPeople[0] === "object" &&
    detailPeople[0] !== null;
  const participants = detailHasPeople
    ? detailPeople
    : listItem.participants;
  return { ...listItem, ...detail, id: listItem.id, participants };
};

export const getCareWatchAllPageChecked = (state) => {
  const { pageItems } = getCareWatchQueueView(state);
  const checkedIds = getCareWatchCheckedIds(state);
  return (
    pageItems.length > 0 && pageItems.every((item) => checkedIds.includes(item.id))
  );
};

export const getCareWatchHasActiveFilters = (state) => {
  const filters = getCareWatchFilters(state);
  const defaults = getDefaultFilters();
  const activeKpi = getCareWatchActiveKpi(state);
  const categoryActive =
    filters.view !== CARE_WATCH_VIEWS.DOCUMENTATION &&
    selectedCategories(filters.category).length > 0;
  const riskActive =
    filters.view !== CARE_WATCH_VIEWS.DOCUMENTATION &&
    (filters.risk || "all") !== defaults.risk;
  return (
    categoryActive ||
    riskActive ||
    (filters.issue || "all") !== defaults.issue ||
    (filters.liveState || "all") !== (defaults.liveState || "all") ||
    filters.status !== defaults.status ||
    filters.fromDate !== defaults.fromDate ||
    filters.toDate !== defaults.toDate ||
    Boolean((filters.search || "").trim()) ||
    Boolean(activeKpi)
  );
};

export const getCareWatchMatchCaption = (state) => {
  const filters = getCareWatchFilters(state);
  if (
    filters.view !== CARE_WATCH_VIEWS.INCIDENT ||
    selectedCategories(filters.category).length === 0
  ) {
    return null;
  }
  const { total } = getCareWatchQueueView(state);
  return `${total} signal${total === 1 ? "" : "s"} matches this category + Needs follow-up`;
};

export const getCareWatchUpdatedByPusher = (state) =>
  getCareWatchState(state)?.get("updatedByPusher") || null;
